import { useContext, useState } from "react";
import api from "../../api";
import { useToast } from "../ToastContext";
import { LoadingContext } from "../LoadingContext";
import { AuthContext } from "../AuthContext";
import { get, set } from "lodash";

export interface CustoType {
    id: number
    description?: string;
    proportionalCurrentStudents?: boolean;
    individualValue?: number;
    currentBalance?: number;
    requestDate?: string | null;
    deliveryDate?: string | null;
    paymentDate?: string | null;
    receiver?: string;
    observation?: string;
    isPaid?: boolean | null;
    empresaId: number;
    contratoId: number;
    tipoCustoId: number;

    tipoCustoName?: string;
    tipoCustoIsFinancial?: boolean;

    hasReceiptAttachment: boolean;
}

export interface CreateCustoType {
    id?: number;
    description?: string;
    proportionalCurrentStudents?: boolean;
    individualValue?: number;
    currentBalance?: number;
    requestDate?: string | null;
    deliveryDate?: string | null;
    paymentDate?: string | null;
    receiver?: string;
    observation?: string;
    isPaid?: boolean | null;
    receiptAttachment?: string;
    empresaId: number;
    contratoId: number;
    tipoCustoId: number;
}

export interface EditCustoType {
    id: number;
    description?: string;
    proportionalCurrentStudents?: boolean;
    individualValue?: number;
    currentBalance?: number;
    requestDate?: string | null;
    deliveryDate?: string | null;
    paymentDate?: string | null;
    receiver?: string;
    receiptAttachment?: string;
    observation?: string;
    isPaid?: boolean | null;
    tipoCustoId: number;
    hasReceiptAttachment?: boolean;
}

interface TotaisCustoType {
    totalValor: number;
    totalSaldo: number;
    saldoFinal: number;
}

export interface CreateCustosComContaPagarRequest {
    custos: CreateCustoType[];
    empresaId: number;
    contaFinanceiraId: number;
    categoriaFinanceiraId: number;
    formaPagamentoId: number;
    vencimento: string;
    documento: string;
    historico?: string;
    bandeira?: string;
    tipoContaPagarId: number;
}

export default function useCusto() {
    const showToast = useToast();
    const { setLoading } = useContext(LoadingContext);
    const { handleLogout, objUserTk } = useContext(AuthContext);

    const [listCusto, setListCusto] = useState<CustoType[]>([]);
    const [custoPageCount, setCustoPageCount] = useState<number>(0);
    const [initialListCusto, setInitialListCusto] = useState<CustoType[]>([]);

    const [listCustoFinanceiro, setListCustoFinanceiro] = useState<CustoType[]>([]);
    const [custoFinanceiroPageCount, setCustoFinanceiroPageCount] = useState<number>(0);
    const [initialListCustoFinanceiro, setInitialListCustoFinanceiro] = useState<CustoType[]>([]);

    const [listCustoGeral, setListCustoGeral] = useState<CustoType[]>([]);
    const [custoGeralPageCount, setCustoGeralPageCount] = useState<number>(0);
    const [initialListCustoGeral, setInitialListCustoGeral] = useState<CustoType[]>([]);

    const [totaisFinanceiro, setTotaisFinanceiro] = useState<TotaisCustoType>({ totalValor: 0, totalSaldo: 0, saldoFinal: 0 });
    const [totaisGeral, setTotaisGeral] = useState<TotaisCustoType>({ totalValor: 0, totalSaldo: 0, saldoFinal: 0 });

    const [custo, setCusto] = useState<CustoType>({
        id: 0,
        description: "",
        proportionalCurrentStudents: false,
        individualValue: 0,
        currentBalance: 0,
        requestDate: "",
        deliveryDate: "",
        paymentDate: "",
        receiver: "",
        observation: "",
        isPaid: null,
        empresaId: 0,
        contratoId: 0,
        tipoCustoId: 0,
        tipoCustoName: "",
        tipoCustoIsFinancial: false,
        hasReceiptAttachment: false,
    });

    function createCusto(createCustoType: CreateCustoType): Promise<CreateCustoType | null> {
        setLoading(true);
        console.log("createCustoType: ", createCustoType);
        return api
            .post(`/Custo/create`, createCustoType)
            .then((response) => {
                const createdCusto: CreateCustoType = response.data;
                showToast("success", "Custo criado com sucesso.");
                setLoading(false);
                return createdCusto;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao criar custo. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao criar custo:", error);
                return null;
            });
    }

    function getCustoPaged(
        pageNumber: number,
        ContratoId?: number,
        EmpresaId?: number,
        IsFinancial?: boolean,
        pageSize?: number
    ): Promise<CustoType[] | null> {
        setLoading(true);
        let url = `/Custo/getCustoPaged?pageNumber=${pageNumber}&pageSize=${pageSize}`;

        if (EmpresaId) {
            url += `&EmpresaId=${EmpresaId}`;
        }

        if (ContratoId) {
            url += `&ContratoId=${ContratoId}`;
        }

        if (IsFinancial) {
            url += `&IsFinancial=${IsFinancial}`;
        }

        return api
            .get(url)
            .then((response) => {
                setListCusto(response.data.result);
                setInitialListCusto(response.data.result);
                setCustoPageCount(response.data.pageCount);
                setLoading(false);
                return response.data.result;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao carregar custo. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao carregar custo:", error);
                return null;
            });
    }

    function getCustoFinanceiroPaged(
        pageNumber: number,
        ContratoId?: number,
        EmpresaId?: number,
        pageSize?: number
    ): Promise<CustoType[] | null> {
        setLoading(true);
        let url = `/Custo/getCustoPaged?pageNumber=${pageNumber}&pageSize=${pageSize}&IsFinancial=true`;

        if (EmpresaId) {
            url += `&EmpresaId=${EmpresaId}`;
        }

        if (ContratoId) {
            url += `&ContratoId=${ContratoId}`;
        }

        return api
            .get(url)
            .then((response) => {
                setListCustoFinanceiro(response.data.result);
                setInitialListCustoFinanceiro(response.data.result);
                setCustoFinanceiroPageCount(response.data.pageCount);

                getCustoTotais(ContratoId, EmpresaId, true)
                    .then(totals => totals && setTotaisFinanceiro(totals));

                setLoading(false);
                return response.data.result;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao carregar custo. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao carregar custo:", error);
                return null;
            });
    }

    function getCustoGeralPaged(
        pageNumber: number,
        ContratoId?: number,
        EmpresaId?: number,
        pageSize?: number
    ): Promise<CustoType[] | null> {
        setLoading(true);
        let url = `/Custo/getCustoPaged?pageNumber=${pageNumber}&pageSize=${pageSize}&IsFinancial=false`;

        if (EmpresaId) {
            url += `&EmpresaId=${EmpresaId}`;
        }

        if (ContratoId) {
            url += `&ContratoId=${ContratoId}`;
        }

        return api
            .get(url)
            .then((response) => {
                setListCustoGeral(response.data.result);
                setInitialListCustoGeral(response.data.result);
                setCustoGeralPageCount(response.data.pageCount);

                getCustoTotais(ContratoId, EmpresaId, false)
                    .then(totals => totals && setTotaisGeral(totals));

                setLoading(false);
                return response.data.result;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao carregar custo. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao carregar custo:", error);
                return null;
            });
    }

    function updateCusto(updateCustoData: EditCustoType): Promise<EditCustoType | null> {
        setLoading(true);
        return api
            .put(`/Custo/${updateCustoData.id}`, updateCustoData)
            .then((response) => {
                showToast("success", "Custo atualizada com sucesso.");
                setLoading(false);
                return response.data;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao atualizar custo. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao atualizar custo:", error);
                return null;
            });
    }

    function deleteCusto(custoId: number): Promise<CustoType | null> {
        setLoading(true);
        return api
            .delete(`/Custo/delete/${custoId}`)
            .then(() => {
                setCusto({
                    id: 0,
                    description: "",
                    proportionalCurrentStudents: false,
                    individualValue: 0,
                    currentBalance: 0,
                    requestDate: "",
                    deliveryDate: "",
                    paymentDate: "",
                    receiver: "",
                    observation: "",
                    isPaid: false,
                    empresaId: 0,
                    contratoId: 0,
                    tipoCustoId: 0,
                    tipoCustoName: "",
                    tipoCustoIsFinancial: false,
                    hasReceiptAttachment: false,
                });
                showToast("success", "Custo deletada com sucesso.");
                setLoading(false);
                return null;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao deletar custo. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao deletar custo:", error);
                return null;
            });
    }

    function exportCustoBytes(
        ContratoId?: number,
        EmpresaId?: number,
        IsFinancial?: boolean
    ): Promise<void> {
        setLoading(true);

        let url = `/Custo/exportToPdf?pageNumber=1&pageSize=999999`;

        if (EmpresaId) {
            url += `&EmpresaId=${EmpresaId}`;
        }
        if (ContratoId) {
            url += `&ContratoId=${ContratoId}`;
        }
        if (IsFinancial !== undefined) {
            url += `&IsFinancial=${IsFinancial}`;
        }

        return api
            .get(url, { responseType: "blob" })
            .then((response) => {
                const file = new Blob([response.data], { type: "application/pdf" });
                const fileURL = URL.createObjectURL(file);

                const link = document.createElement("a");
                link.href = fileURL;
                link.setAttribute("download", "custos_exportados.pdf");
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                showToast("success", "PDF baixado com sucesso!");
                setLoading(false);
            })
            .catch((error) => {
                showToast("error", "Erro ao baixar PDF de custos.", error);
                setLoading(false);
                console.error("Erro ao exportar custo PDF:", error);
            });
    }

    function getCustoTotais(
        ContratoId?: number,
        EmpresaId?: number,
        IsFinancial?: boolean
    ): Promise<TotaisCustoType | null> {

        let url = `/Custo/getTotais?`;
        const p: Record<string, any> = {
            EmpresaId,
            ContratoId,
            IsFinancial
        };
        Object.entries(p).forEach(([k, v]) => v !== undefined && (url += `&${k}=${v}`));

        return api.get(url)
            .then(res => res.data as TotaisCustoType)
            .catch(err => {
                console.error("Erro ao buscar totais:", err);
                return null;
            });
    }

    function enviarCustoParaFinanceiro(custoId: number): Promise<boolean> {
        setLoading(true);
        return api
            .post(`/Custo/${custoId}/enviar-para-financeiro`)
            .then(() => {
                showToast("success", "Custo enviado para o financeiro com sucesso.");
                setLoading(false);
                return true;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        error?.response?.data || "Erro ao enviar custo para o financeiro.",
                        error
                    );
                }
                setLoading(false);
                return false;
            });
    }

    function createCustosWithContaPagar(payload: CreateCustosComContaPagarRequest): Promise<any[] | null> {
        setLoading(true);
        return api
            .post(`/Custo/create-batch-with-conta-pagar`, payload)
            .then((response) => {
                showToast("success", "Custos e Conta a Pagar criados com sucesso.");
                setLoading(false);
                return response.data;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast("error", "Sessão desconectada. Faça login novamente.", error);
                } else {
                    showToast("error", "Erro ao criar custos com conta a pagar.", error);
                }
                setLoading(false);
                console.error("Erro em createCustosWithContaPagar:", error);
                return null;
            });
    }

    function getCustoAttachment(custoId: number) {
            setLoading(true);
            return api
                .get(`/Custo/${custoId}/anexo`, { responseType: "blob" })
                .then((response) => {
                    if (!response || !response.data || response.data.size === 0) {
                        showToast("error", "Ainda não foi anexado nenhum documento para esse custo.");
                        return;
                    }
    
                    const blob = new Blob([response.data], { type: "application/pdf" });
                    const url = window.URL.createObjectURL(blob);
    
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `custo_${custoId}.pdf`;
                    document.body.appendChild(a);
                    a.click();
    
                    showToast("success", "Download iniciado!");
                    requestAnimationFrame(() => {
                    setTimeout(() => {
                        window.URL.revokeObjectURL(url);
                        setLoading(false);
                    }, 100);
                });
                })
                .catch((error) => {
                    if (error.response && error.response.status === 404) {
                        showToast("error", "Ainda não foi anexado um documento a esse custo.");
                    } else {
                        showToast("error", "Erro ao baixar custo. Verifique novamente mais tarde.");
                    }
                });
        }

    return {
        createCusto,
        getCustoPaged,
        updateCusto,
        deleteCusto,
        setListCusto,
        listCusto,
        setCustoPageCount,
        custoPageCount,
        setInitialListCusto,
        initialListCusto,
        setCusto,
        custo,

        getCustoFinanceiroPaged,
        listCustoFinanceiro,
        setListCustoFinanceiro,
        custoFinanceiroPageCount,
        setCustoFinanceiroPageCount,
        initialListCustoFinanceiro,
        setInitialListCustoFinanceiro,

        getCustoGeralPaged,
        listCustoGeral,
        setListCustoGeral,
        custoGeralPageCount,
        setCustoGeralPageCount,
        initialListCustoGeral,
        setInitialListCustoGeral,
        exportCustoBytes,
        totaisFinanceiro,
        totaisGeral,
        getCustoTotais,

        enviarCustoParaFinanceiro,
        createCustosWithContaPagar,

        getCustoAttachment,
    };
}
